import { useCallback, useMemo } from 'react';

import { getIsToolPart, patchApprovalResponse, patchToolOutput, toUiMessages } from '@aline/common';
import type {
  AgentContinuationResponse,
  AiSdkUiMessage,
  ToolApprovalResponse,
  ToolOutputResponse,
} from '@aline/common';
import type { ApolloCache } from '@apollo/client';
import { gql } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { Apollo } from 'src/gql/generated';

type AgentMessageData = Apollo.GetAgentThreadQuery['agentThread']['messages'][number]['data'];

const MESSAGE_DATA_FRAGMENT = gql`
  fragment AlineChatAdapterMessageData on AgentMessage {
    id
    data
  }
`;

export type AlineChatAdapter = {
  addToolApprovalResponse: (input: ToolApprovalResponse) => Promise<void>;
  addToolOutput: (input: ToolOutputResponse) => Promise<void>;
  addToolOutputs: (inputs: readonly ToolOutputResponse[]) => Promise<void>;
};

export function useAlineChatAdapter(threadId: UUID | null): AlineChatAdapter {
  const { data } = Apollo.useGetAgentThreadQuery({
    skip: !threadId,
    variables: { id: threadId ?? '' },
  });
  const [continueAgentChat] = Apollo.useContinueAgentChatMutation();
  const { enqueueSnackbar } = useSnackbar();
  const agentMessages = useMemo(
    () => data?.agentThread.messages ?? [],
    [data?.agentThread.messages]
  );
  const messages = useMemo(() => toUiMessages(agentMessages), [agentMessages]);

  const addToolApprovalResponse = useCallback(
    async (input: ToolApprovalResponse) => {
      if (!threadId) return;

      try {
        const patchedMessages = patchApprovalResponse(messages, input);
        const patchedMessage = patchedMessages.find(message => message.id === input.messageId);
        if (!patchedMessage) return;

        await continueAgentChat(
          buildContinueAgentChatOptions({
            patchedMessage,
            response: { approval: input, kind: 'approval' },
            threadId,
          })
        );
      } catch (error) {
        reportToolResponseFailure(error, enqueueSnackbar);
      }
    },
    [continueAgentChat, enqueueSnackbar, messages, threadId]
  );

  const addToolOutputs = useCallback(
    async (inputs: readonly ToolOutputResponse[]) => {
      if (!threadId) return;
      const firstInput = inputs.at(0);
      if (!firstInput) return;

      try {
        let patchedMessages = messages;
        for (const input of inputs) patchedMessages = patchToolOutput(patchedMessages, input);

        const patchedMessage = firstInput.messageId
          ? patchedMessages.find(message => message.id === firstInput.messageId)
          : findMessageWithToolCall(patchedMessages, firstInput.toolCallId);
        if (!patchedMessage) return;

        await continueAgentChat(
          buildContinueAgentChatOptions({
            patchedMessage,
            response: { kind: 'tool-outputs', outputs: [...inputs] },
            threadId,
          })
        );
      } catch (error) {
        reportToolResponseFailure(error, enqueueSnackbar);
      }
    },
    [continueAgentChat, enqueueSnackbar, messages, threadId]
  );
  const addToolOutput = useCallback(
    async (input: ToolOutputResponse) => addToolOutputs([input]),
    [addToolOutputs]
  );

  return {
    addToolApprovalResponse,
    addToolOutput,
    addToolOutputs,
  };
}

/**
 * Optimistically patch the answered message into the cache via the mutation's
 * `update`. Apollo applies it on an optimistic layer and re-applies it once the
 * server acks; if `continueAgentChat` fails it discards the layer, reverting the
 * approval/tool-output back to its pending state so the user can retry.
 *
 * The mutation itself carries only the explicit answer — the server locates
 * and patches the paused parts on the row-locked message; the locally patched
 * message exists purely for the optimistic cache write.
 */
function buildContinueAgentChatOptions({
  patchedMessage,
  response,
  threadId,
}: {
  patchedMessage: AiSdkUiMessage;
  response: AgentContinuationResponse;
  threadId: UUID;
}) {
  return {
    optimisticResponse: {
      __typename: 'Mutation' as const,
      continueAgentChat: {
        __typename: 'AgentResponse' as const,
        messageId: patchedMessage.id,
        prompt: '',
        threadId,
      },
    },
    update: (cache: ApolloCache) => {
      patchAgentMessageData({ cache, patchedMessage });
    },
    variables: {
      input: { messageId: patchedMessage.id, response, threadId },
    },
  };
}

function patchAgentMessageData({
  cache,
  patchedMessage,
}: {
  cache: ApolloCache;
  patchedMessage: AiSdkUiMessage;
}) {
  const id = cache.identify({ __typename: 'AgentMessage', id: patchedMessage.id });
  /**
   * Read the merge base from the cache (the source of truth) rather than a
   * React snapshot of the thread query, so we never drop sibling `data` fields
   * (toolCalls, suggestionStates, …) when the snapshot is missing the row.
   */
  const existing = cache.readFragment<{ data: AgentMessageData }>({
    fragment: MESSAGE_DATA_FRAGMENT,
    id,
  });
  cache.writeFragment({
    data: {
      __typename: 'AgentMessage',
      data: {
        ...existing?.data,
        uiParts: patchedMessage.parts,
      },
      id: patchedMessage.id,
    },
    fragment: MESSAGE_DATA_FRAGMENT,
    id,
  });
}

function reportToolResponseFailure(
  error: unknown,
  enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar']
) {
  Sentry.captureException(error);
  enqueueSnackbar('Could not send your response. Please try again.', { variant: 'error' });
}

function findMessageWithToolCall(messages: AiSdkUiMessage[], toolCallId: string) {
  return messages.find(message =>
    message.parts.some(part => getIsToolPart(part) && part.toolCallId === toolCallId)
  );
}
